import React, { useState } from 'react';
import { useAuthentication } from '../modules/auth/services/auth.provider';
import { MenuList } from './menuItems';
import { Button, Grid2 } from '@mui/material';
import { useNavigate } from 'react-router';
import { logout } from '../modules/auth/services/auth.services';
import {
  AddIcon,
  ArticleIcon,
  AssetIcon,
  AuthorIcon,
  BlogIcon,
  BookIcon,
  BrandIcon,
  DashboardIcon,
  DownIcon,
  EditIcon,
  EditProfileIcon,
  EventIcon,
  FacebookIcon,
  FilledArticleIcon,
  FilledAssetIcon,
  FilledAuthorIcon,
  FilledBlogIcon,
  FilledBookIcon,
  FilledBrandIcon,
  FilledDashboardIcon,
  FilledEditProfileIcon,
  FilledEventIcon,
  FilledFacebookIcon,
  FilledIdentityIcon,
  FilledInstagramIcon,
  FilledLinkedInIcon,
  FilledLocationIcon,
  FilledLogoutIcon,
  FilledPersonIcon,
  FilledPostIcon,
  FilledQuoteIcon,
  FilledSubTopicIcon,
  FilledThemeIcon,
  FilledTopicIcon,
  FilledTwitterIcon,
  FilledVideoIcon,
  FilledVlogIcon,
  FilledYoutubeIcon,
  FinalIcon,
  FrontEndAnalyticsIcon,
  IdentityIcon,
  InstagramIcon,
  LinkedInIcon,
  LocationIcon,
  LogoutIcon,
  MarketingPlatformIcon,
  PersonIcon,
  PostIcon,
  PublishIcon,
  QuoteIcon,
  SubTopicIcon,
  ThemeIcon,
  TopicIcon,
  TwitterIcon,
  UpIcon,
  UploadIcon,
  VersionIcon,
  VideoIcon,
  VlogIcon,
  YoutubeIcon,
} from '../modules/shared/components/AppIcons';
import logo from '../assets/images/logo.svg';
import { makeStyles } from '@mui/styles';
import { MenuType } from '../data/types/menus.interface';

const useStyles = makeStyles({
  logo: {
    borderRadius: '50%',
  },
  menuButton: {
    justifyContent: 'flex-start !important',
    fontSize: '16px !important',
    '&:hover': {
      background: 'none',
      color: 'inherit',
      border: 'none',
    },
    '& .MuiButton-startIcon': {
      margin: '0px !important',
    },
    '& .MuiButton-endIcon': {
      position: 'absolute',
      right: 0,
    },
    '&.header': {
      marginLeft: '-8px',
      width: '100%',
    },
    '& path': {
      fill: '#303340 !important',
    },
  },
  menuIcon: {
    fontSize: '40px !important',
    '& path': {
      fill: 'inherit !important',
      '&:active': {
        fill: '#1264E6 !important',
      },
    },
  },
  menuItem: {
    height: '40px',
    cursor: 'pointer',
    marginBottom: '5px',
    marginRight: '11px',
    '& g path': {
      fill: '#303340 !important',
    },
    '&.active': {
      color: '#1264E6 !important',
      backgroundColor: '#E7EDFF !important',
      borderRadius: '12px',
      '& g path': {
        fill: '#1264E6 !important',
      },
    },
    '& .filledIcon': {
      display: 'flex',
      width: '42px',
      height: '40px',
      fontSize: '14px !important',
      padding: '12px 12px 12px 14px',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  subMenuItem: {
    height: '40px',
    cursor: 'pointer',
    marginRight: '11px',
    '& g path': {
      fill: '#303340 !important',
    },
    '&.active': {
      color: '#1264E6 !important',
      backgroundColor: '#E7EDFF !important',
      borderRadius: '12px',
      '& g path': {
        fill: '#1264E6 !important',
      },
    },
  },
});

const NavigationSideBar: React.FC = () => {
  const auth = useAuthentication();
  const classes = useStyles();
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [mainMenuItems] = useState<any>(Object.values(MenuList));
  const [anchorEl, setAnchorEl] = React.useState(false);
  const [dashboardAnchorEl, setDashboardAnchorEl] = React.useState(false);
  const [ccAnchorEl, setCcAnchorEl] = React.useState(false);
  const [themeAnchorEl, setThemeAnchorEl] = React.useState(false);
  const [assetTypeAnchorEl, setAssetTypeAnchorEl] = React.useState(false);
  const [marketingPlatformAnchorEl, setMarketingPlatformAnchorEl] = React.useState(false);
  const [frontEndAnalyticsAnchorEl, setFrontEndAnalyticsAnchorEl] = React.useState(false);
  let activePath = window.location.pathname;

  const logOut = (event: React.MouseEvent) => {
    event.preventDefault();
    logout().then(() => {
      auth.logout();
      window.location.href = '/login';
    });
  };

  const handleClick = (event: React.MouseEvent, menu: MenuType) => {
    switch (menu.menuId) {
      case 'dashboard':
      case 'topic':
      case 'sub-topic':
      case 'brand':
      case 'person':
      case 'identity':
      case 'location':
      case 'event':
      case 'author':
      case 'editProfile':
      case 'vlog':
      case 'article':
      case 'video':
      case 'blog':
      case 'quote':
      case 'post':
      case 'book':
      case 'youtube':
      case 'facebook':
      case 'instagram':
      case 'linkedIn':
      case 'twitter':
      case 'content-collection':
      case 'pre-final-file':
      case 'final-file':
        if (auth.currentUser?.role.name === 'marketer') {
          navigate('/unauthorized');
        } else {
          navigate(menu.path);
        }
        break;
      case 'content-marketing':
      case 'marketing-analytics':
        if (!['marketer', 'admin'].includes(auth.currentUser?.role.name || '')) {
          navigate('/unauthorized');
        } else {
          navigate(menu.path);
        }
        break;
      case 'logout':
        logOut(event);
        break;
    }
  };

  const getMenuIcon = (id: string, isFilled: boolean) => {
    switch (id) {
      case 'topic':
      case 'website':
        return isFilled ? <FilledTopicIcon className={classes.menuIcon} /> : <TopicIcon className={classes.menuIcon} />;
      case 'sub-topic':
      case 'appAnalytics':
        return isFilled ? (
          <FilledSubTopicIcon className={classes.menuIcon} />
        ) : (
          <SubTopicIcon className={classes.menuIcon} />
        );
      case 'brand':
        return isFilled ? <FilledBrandIcon className={classes.menuIcon} /> : <BrandIcon className={classes.menuIcon} />;
      case 'person':
        return isFilled ? (
          <FilledPersonIcon className={classes.menuIcon} />
        ) : (
          <PersonIcon className={classes.menuIcon} />
        );
      case 'identity':
        return isFilled ? (
          <FilledIdentityIcon className={classes.menuIcon} />
        ) : (
          <IdentityIcon className={classes.menuIcon} />
        );
      case 'location':
        return isFilled ? (
          <FilledLocationIcon className={classes.menuIcon} />
        ) : (
          <LocationIcon className={classes.menuIcon} />
        );
      case 'event':
        return isFilled ? <FilledEventIcon className={classes.menuIcon} /> : <EventIcon className={classes.menuIcon} />;
      case 'author':
        return isFilled ? (
          <FilledAuthorIcon className={classes.menuIcon} />
        ) : (
          <AuthorIcon className={classes.menuIcon} />
        );
      case 'vlog':
        return isFilled ? <FilledVlogIcon className={classes.menuIcon} /> : <VlogIcon className={classes.menuIcon} />;
      case 'article':
        return isFilled ? (
          <FilledArticleIcon className={classes.menuIcon} />
        ) : (
          <ArticleIcon className={classes.menuIcon} />
        );
      case 'video':
        return isFilled ? <FilledVideoIcon className={classes.menuIcon} /> : <VideoIcon className={classes.menuIcon} />;
      case 'blog':
        return isFilled ? <FilledBlogIcon className={classes.menuIcon} /> : <BlogIcon className={classes.menuIcon} />;
      case 'quote':
        return isFilled ? <FilledQuoteIcon className={classes.menuIcon} /> : <QuoteIcon className={classes.menuIcon} />;
      case 'post':
        return isFilled ? <FilledPostIcon className={classes.menuIcon} /> : <PostIcon className={classes.menuIcon} />;
      case 'book':
        return isFilled ? <FilledBookIcon className={classes.menuIcon} /> : <BookIcon className={classes.menuIcon} />;
      case 'youtube':
        return isFilled ? (
          <FilledYoutubeIcon className={classes.menuIcon} />
        ) : (
          <YoutubeIcon className={classes.menuIcon} />
        );
      case 'facebook':
        return isFilled ? (
          <FilledFacebookIcon className={classes.menuIcon} />
        ) : (
          <FacebookIcon className={classes.menuIcon} />
        );
      case 'instagram':
        return isFilled ? (
          <FilledInstagramIcon className={classes.menuIcon} />
        ) : (
          <InstagramIcon className={classes.menuIcon} />
        );
      case 'linkedIn':
        return isFilled ? (
          <FilledLinkedInIcon className={classes.menuIcon} />
        ) : (
          <LinkedInIcon className={classes.menuIcon} />
        );
      case 'twitter':
        return isFilled ? (
          <FilledTwitterIcon className={classes.menuIcon} />
        ) : (
          <TwitterIcon className={classes.menuIcon} />
        );
      case 'editProfile':
        return isFilled ? (
          <FilledEditProfileIcon className={classes.menuIcon} />
        ) : (
          <EditProfileIcon className={classes.menuIcon} />
        );
      case 'logout':
        return isFilled ? (
          <FilledLogoutIcon className={classes.menuIcon} />
        ) : (
          <LogoutIcon className={classes.menuIcon} />
        );
      case 'content-collection':
        return <UploadIcon className={classes.menuIcon} />;
      case 'content-creation':
        return <AddIcon className={classes.menuIcon} />;
      case 'content-marketing':
        return <EditIcon className={classes.menuIcon} />;
      case 'marketing-analytics':
        return <PublishIcon className={classes.menuIcon} />;
      case 'pre-final-file':
        return <VersionIcon className={classes.menuIcon} sx={{ fontSize: '28px !important', mr: '4px' }} />;
      case 'final-file':
        return <FinalIcon className={classes.menuIcon} sx={{ fontSize: '28px !important', mr: '4px' }} />;
      default:
        break;
    }
  };

  const getMenuItem = (menuItem: MenuType, key: string) => {
    return (
      <Grid2
        key={key}
        container
        alignItems="center"
        className={`${classes.subMenuItem} ${activePath === menuItem.path ? 'active' : ''} `}
        style={{ textDecoration: 'none' }}
        onClick={(e) => {
          activePath = window.location.pathname;
          handleClick(e, menuItem);
        }}
      >
        {getMenuIcon(menuItem.menuId, activePath === menuItem.path)}
        <span>{menuItem.label}</span>
      </Grid2>
    );
  };

  const getContentCreationSubMenu = (menuItem: MenuType) => {
    return (
      <>
        <Button
          className={`${classes.menuButton} header `}
          fullWidth
          sx={{
            color: activePath === menuItem.path ? '#1264E6' : '#303340',
            textTransform: 'none',
            fontFamily: 'Lato',
            padding: '0 8px',
          }}
          startIcon={getMenuIcon(menuItem.menuId, activePath === menuItem.path)}
          endIcon={
            <Grid2 container mr="11px">
              {!ccAnchorEl ? <DownIcon style={{ fontSize: '35px' }} /> : <UpIcon style={{ fontSize: '35px' }} />}
            </Grid2>
          }
          onClick={() => {
            setCcAnchorEl(!ccAnchorEl);
          }}
        >
          Content Creation
        </Button>
        <Grid2 container>
          {ccAnchorEl && (
            <Grid2 container direction="column" pl={3} width="100%">
              {menuItem.subMenu.map((option: MenuType, index: number) => {
                return getMenuItem(option, `cc-${index}`);
              })}
            </Grid2>
          )}
        </Grid2>
      </>
    );
  };

  return (
    <Grid2
      container
      direction="column"
      position="fixed"
      top={0}
      left={0}
      height="100vh"
      width="275px"
      padding="24px 0px 0px 24px"
      sx={{
        background: '#F8F9FA',
        overflow: 'hidden',
        borderRight: '1px solid #E5E7EB',
        zIndex: 40,
      }}
      color="#303340"
    >
      <Grid2 container spacing={2}>
        <img src={logo} width="45px" height="45px" className={classes.logo} alt="appLogo" />
      </Grid2>
      <Grid2
        container
        direction="column"
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Grid2 container direction="column" gap="0 0">
          {mainMenuItems && (
            <>
              {/* Dashboard nav menu */}
              <Grid2
                container
                alignItems="center"
                className={`${classes.menuItem} ${activePath.includes('/dashboard') ? 'active' : ''} `}
              >
                <Button
                  className={`${classes.menuButton} header `}
                  fullWidth
                  sx={{
                    color: activePath === '/dashboard' ? '#1264E6' : '#303340',
                    textTransform: 'none',
                    fontFamily: 'Lato',
                    padding: '0 8px',
                  }}
                  startIcon={
                    activePath.includes('/dashboard') ? (
                      <FilledDashboardIcon className={classes.menuIcon} />
                    ) : (
                      <DashboardIcon className={classes.menuIcon} />
                    )
                  }
                  endIcon={
                    <Grid2 container>
                      {!dashboardAnchorEl ? (
                        <DownIcon style={{ fontSize: '35px' }} />
                      ) : (
                        <UpIcon style={{ fontSize: '35px' }} />
                      )}
                    </Grid2>
                  }
                  onClick={() => {
                    activePath = window.location.pathname;
                    navigate('/dashboard');
                    setDashboardAnchorEl(!dashboardAnchorEl);
                  }}
                >
                  Dashboard
                </Button>
              </Grid2>
              <Grid2 container>
                {dashboardAnchorEl && (
                  <Grid2 container direction="column" pl={3} width="100%">
                    {mainMenuItems[0].map((option: MenuType, index: number) => {
                      if (option.menuId === 'content-creation') {
                        return getContentCreationSubMenu(option);
                      }
                      return getMenuItem(option, `dashboard-${index}`);
                    })}
                  </Grid2>
                )}
              </Grid2>

              {/* Theme nav menu */}
              <Grid2
                container
                alignItems="flex-start"
                direction="column"
                className={`${classes.menuItem} ${activePath === '/theme' ? 'active' : ''} `}
              >
                <Button
                  className={`${classes.menuButton} header `}
                  fullWidth
                  sx={{
                    color: activePath === '/theme' ? '#1264E6' : '#303340',
                    textTransform: 'none',
                    fontFamily: 'Lato',
                    padding: '0 8px',
                  }}
                  startIcon={
                    activePath === '/theme' ? (
                      <FilledThemeIcon className={classes.menuIcon} />
                    ) : (
                      <ThemeIcon className={classes.menuIcon} />
                    )
                  }
                  endIcon={
                    <Grid2 container>
                      {!themeAnchorEl ? (
                        <DownIcon style={{ fontSize: '35px' }} />
                      ) : (
                        <UpIcon style={{ fontSize: '35px' }} />
                      )}
                    </Grid2>
                  }
                  onClick={() => {
                    activePath = window.location.pathname;
                    navigate('/theme');
                    setThemeAnchorEl(!themeAnchorEl);
                  }}
                >
                  Theme
                </Button>
              </Grid2>
              <Grid2 container>
                {themeAnchorEl && (
                  <Grid2 container direction="column" pl={3} width="100%">
                    {mainMenuItems[1].map((option: MenuType, index: number) => {
                      return getMenuItem(option, `theme-${index}`);
                    })}
                  </Grid2>
                )}
              </Grid2>

              {/* Tags nav menu */}
              <Grid2 container direction="column">
                {mainMenuItems[2].map((option: MenuType, index: number) => {
                  return (
                    <Grid2
                      container
                      direction="column"
                      className={`${classes.menuItem} ${activePath === option.path ? 'active' : ''} `}
                    >
                      {getMenuItem(option, `tags-${index}`)}
                    </Grid2>
                  );
                })}
              </Grid2>

              {/* contentType menu items */}
              <Grid2
                container
                alignItems="center"
                className={`${classes.menuItem} ${activePath === '/asset-content-type' ? 'active' : ''} `}
              >
                <Button
                  fullWidth
                  sx={{ color: '#303340', textTransform: 'none', fontFamily: 'Lato', width: '234px' }}
                  startIcon={
                    activePath === '/asset-content-type' ? (
                      <FilledAssetIcon className={classes.menuIcon} />
                    ) : (
                      <AssetIcon className={classes.menuIcon} />
                    )
                  }
                  endIcon={
                    !assetTypeAnchorEl ? (
                      <DownIcon style={{ fontSize: '35px' }} />
                    ) : (
                      <UpIcon style={{ fontSize: '35px' }} />
                    )
                  }
                  className={`${classes.menuButton} header ${classes.menuItem} ${activePath === '/asset-content-type' ? 'active' : ''
                    }`}
                  onClick={() => {
                    activePath = window.location.pathname;
                    navigate('/asset-content-type');
                    setAssetTypeAnchorEl(!assetTypeAnchorEl);
                  }}
                >
                  Asset, Content Type
                </Button>
              </Grid2>
              <Grid2 container>
                {assetTypeAnchorEl && (
                  <Grid2 container direction="column" pl={3} width="100%">
                    {mainMenuItems[3].map((option: MenuType, index: number) => {
                      return getMenuItem(option, `asset-content-type-${index}`);
                    })}
                  </Grid2>
                )}
              </Grid2>

              {/* marketing platform menu items */}
              <Grid2 container direction="column" alignItems="flex-start" className={classes.menuItem}>
                <Button
                  fullWidth
                  className={`${classes.menuButton} header `}
                  sx={{ color: '#303340', textTransform: 'none', fontFamily: 'Lato' }}
                  startIcon={<MarketingPlatformIcon className={classes.menuIcon} />}
                  endIcon={
                    !marketingPlatformAnchorEl ? (
                      <DownIcon style={{ fontSize: '35px' }} />
                    ) : (
                      <UpIcon style={{ fontSize: '35px' }} />
                    )
                  }
                  onClick={() => {
                    activePath = window.location.pathname;
                    setMarketingPlatformAnchorEl(!marketingPlatformAnchorEl);
                  }}
                >
                  Marketing Platform
                </Button>
              </Grid2>
              <Grid2 container mt={1}>
                {marketingPlatformAnchorEl && (
                  <Grid2 container direction="column" pl={3} width="100%">
                    {mainMenuItems[4].map((option: MenuType, index: number) => {
                      return getMenuItem(option, `profile-${index}`);
                    })}
                  </Grid2>
                )}
              </Grid2>

              {/* analytics menu item */}
              <Grid2 container direction="column" alignItems="flex-start" className={classes.menuItem}>
                <Button
                  fullWidth
                  className={`${classes.menuButton} header`}
                  sx={{ color: '#303340', textTransform: 'none', fontFamily: 'Lato' }}
                  startIcon={<FrontEndAnalyticsIcon className={classes.menuIcon} />}
                  endIcon={
                    !frontEndAnalyticsAnchorEl ? (
                      <DownIcon style={{ fontSize: '35px' }} />
                    ) : (
                      <UpIcon style={{ fontSize: '35px' }} />
                    )
                  }
                  onClick={() => {
                    activePath = window.location.pathname;
                    setFrontEndAnalyticsAnchorEl(!frontEndAnalyticsAnchorEl);
                  }}
                >
                  Front-end Analytics
                </Button>
              </Grid2>
              <Grid2 container>
                {frontEndAnalyticsAnchorEl && (
                  <Grid2 container direction="column" pl={3} width="100%">
                    {mainMenuItems[5].map((option: MenuType, index: number) => {
                      return getMenuItem(option, `profile-${index}`);
                    })}
                  </Grid2>
                )}
              </Grid2>
            </>
          )}
        </Grid2>
      </Grid2>

      {/* Profile menu items */}
      <Grid2 container direction="column" alignItems="flex-start" gap="0 0" mb={2}>
        <Button
          fullWidth
          className={`${classes.menuButton} header ${classes.menuItem} ${activePath === '/profile' ? 'active' : ''}`}
          sx={{ color: '#303340', textTransform: 'none', fontFamily: 'Lato' }}
          endIcon={!anchorEl ? <DownIcon style={{ fontSize: '35px' }} /> : <UpIcon style={{ fontSize: '35px' }} />}
          onClick={() => {
            activePath = window.location.pathname;
            setAnchorEl(!anchorEl);
          }}
        >
          <img src={`${user?.profileImageURL}?raw=1`} alt="Preview" style={{ height: 47, width: 47 }} />
          <span style={{ marginLeft: '8px' }}>{`${user?.firstName} ${user?.lastName}`}</span>
        </Button>
        {anchorEl && (
          <Grid2 container direction="column" pl={3} gap="0 0" width="100%">
            {mainMenuItems[6].map((option: MenuType, index: number) => {
              return getMenuItem(option, `profile-${index}`);
            })}
          </Grid2>
        )}
      </Grid2>
    </Grid2>
  );
};

export default NavigationSideBar;
